/**
 * Migration 0095 — "Requests" section at the top of the Today board.
 *
 * Lists customer bookings in status 'requested' (FIFO) with Approve /
 * Decline actions. Self-contained: fetches via `@/lib/booking_requests_api`
 * (raw fetch, NOT `@/lib/api`, so the existing Today test mocks stay intact)
 * and renders nothing when the queue is empty or the caller can't see it
 * (403 / no capability / fetch failure).
 *
 * `refreshKey` lets the parent re-fetch on realtime booking events without
 * this component opening its own WebSocket.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import {
  BookingRequestsApiError,
  approveBookingRequest,
  declineBookingRequest,
  listBookingRequests,
} from '@/lib/booking_requests_api';
import { formatPriceMinor } from '@/lib/format';
import type { BookingRequestRow } from '@/lib/types';
import { CheckCircle2, Clock, Inbox, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  facilityId: string | null;
  /** Bump to force a re-fetch (e.g. on realtime BOOKING_* events). */
  refreshKey?: number;
}

const TYPE_LABEL: Record<string, string> = {
  boarding: 'Boarding',
  daycare: 'Daycare',
  grooming: 'Grooming',
  service: 'Service',
};

function formatWhen(iso: string | null, tz: string, locale: string): string {
  if (!iso) return '';
  try {
    const hasTime = /T\d{2}:\d{2}/.test(iso) && !/T00:00(:00)?(\.000)?Z?$/.test(iso);
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      ...(hasTime ? { timeStyle: 'short' } : {}),
      timeZone: tz,
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function hoursUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 3_600_000));
}

export function BookingRequestsPanel({ facilityId, refreshKey = 0 }: Props) {
  const { state } = useAuthContext();
  const me = state.me;
  const capabilities: readonly string[] = me?.capabilities ?? [];
  const canApprove = capabilities.includes('*') || capabilities.includes('bookings.approve');
  const tz = me?.activeFacility?.timezone ?? me?.activeAccount?.defaultTimezone ?? 'UTC';
  const locale = me?.activeAccount?.locale ?? 'en-GB';
  const currency = me?.activeAccount?.currency ?? 'GBP';

  const [rows, setRows] = useState<BookingRequestRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!canApprove) {
      setRows(null);
      return;
    }
    try {
      const res = await listBookingRequests(facilityId);
      setRows(res.requests);
    } catch {
      // 403 / network / no fetch in tests — the section simply doesn't render.
      setRows(null);
    }
  }, [canApprove, facilityId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  async function handleApprove(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await approveBookingRequest(id);
      setRows((prev) => (prev ? prev.filter((r) => r.bookingId !== id) : prev));
    } catch (err) {
      setError(
        err instanceof BookingRequestsApiError && err.status === 409
          ? 'This request was already actioned.'
          : 'Could not approve the request. Please try again.',
      );
      void load();
    } finally {
      setBusyId(null);
    }
  }

  async function handleDecline(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await declineBookingRequest(id, declineReason.trim() || undefined);
      setRows((prev) => (prev ? prev.filter((r) => r.bookingId !== id) : prev));
      setDecliningId(null);
      setDeclineReason('');
    } catch (err) {
      setError(
        err instanceof BookingRequestsApiError && err.status === 409
          ? 'This request was already actioned.'
          : 'Could not decline the request. Please try again.',
      );
      void load();
    } finally {
      setBusyId(null);
    }
  }

  if (!canApprove || rows === null || rows.length === 0) return null;

  return (
    <section
      className="mb-6 rounded-xl border border-warning/40 bg-warning/5 px-5 py-4"
      data-testid="booking-requests"
      aria-labelledby="booking-requests-heading"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2
          id="booking-requests-heading"
          className="flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
        >
          <Inbox className="h-3.5 w-3.5" aria-hidden />
          Booking requests awaiting approval
          <span className="num rounded-full bg-warning/20 px-2 py-0.5 text-[10px] font-semibold text-warning-foreground">
            {rows.length}
          </span>
        </h2>
        <p className="text-xs text-muted-foreground">
          Capacity is held while you decide; unactioned requests expire automatically.
        </p>
      </div>

      {error && (
        <div className="mb-3 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <ul className="divide-y divide-border">
        {rows.map((r) => {
          const busy = busyId === r.bookingId;
          const declining = decliningId === r.bookingId;
          const hrs = hoursUntil(r.expiresAt);
          return (
            <li
              key={r.bookingId}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`booking-request-${r.bookingId}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  <Link
                    to={`/customers/${r.customerId}`}
                    className="hover:underline underline-offset-2"
                  >
                    {r.customerName}
                  </Link>
                  <span className="text-muted-foreground"> · </span>
                  {r.petName}
                  <span className="text-muted-foreground"> · </span>
                  {TYPE_LABEL[r.type] ?? r.type}
                </p>
                <p className="num mt-0.5 text-xs text-muted-foreground">
                  {formatWhen(r.startAt, tz, locale)}
                  {r.endAt ? ` → ${formatWhen(r.endAt, tz, locale)}` : ''}
                  {' · '}
                  {formatPriceMinor(r.totalMinor, currency)}
                  {' · '}
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" aria-hidden />
                    {hrs > 0 ? `expires in ${hrs}h` : 'expiring soon'}
                  </span>
                </p>
                {declining && (
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <label htmlFor={`decline-reason-${r.bookingId}`} className="sr-only">
                      Reason for declining
                    </label>
                    <input
                      id={`decline-reason-${r.bookingId}`}
                      type="text"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder="Optional note to the customer"
                      maxLength={500}
                      className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 sm:max-w-md"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleDecline(r.bookingId)}
                        className="rounded-md bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground press disabled:opacity-50"
                      >
                        {busy ? 'Declining…' : 'Confirm decline'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setDecliningId(null);
                          setDeclineReason('');
                        }}
                        className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground press"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {!declining && (
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void handleApprove(r.bookingId)}
                    aria-label={`Approve request from ${r.customerName}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground press disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    {busy ? 'Approving…' : 'Approve'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setDecliningId(r.bookingId);
                      setDeclineReason('');
                    }}
                    aria-label={`Decline request from ${r.customerName}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground press hover:text-destructive"
                  >
                    <XCircle className="h-3.5 w-3.5" aria-hidden />
                    Decline
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
